import { Dialog } from '@headlessui/react';
import { Transition } from '@headlessui/react';
import { EmptyObject } from 'libs/end-to-end/src/generic';
import { SSORoleMappingsSummary } from 'libs/end-to-end/src/sso';
import React, { Fragment, useMemo, useState } from 'react';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { useParams } from 'react-router';
import { AdminLayout } from 'apps/sigma2-ui/src/components/AdminLayout';
import { AdminTable } from 'apps/sigma2-ui/src/components/AdminLayout';
import { AdminTableRow } from 'apps/sigma2-ui/src/components/AdminLayout';
import { LegacyButton } from 'apps/sigma2-ui/src/components/ui/LegacyButton';
import { LegacyButtonVariant } from 'apps/sigma2-ui/src/components/ui/Buttons';
import { LinkButton } from 'apps/sigma2-ui/src/components/ui/LinkButton';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { IsAliveFn } from 'apps/sigma2-ui/src/lib/hooks/useAlive';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { useAsyncEffect } from 'apps/sigma2-ui/src/lib/hooks/useAsyncEffect';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { AdminBreadcrumbs } from 'apps/sigma2-ui/src/components/ui/AdminBreadcrumbs';

export const AdminSSORoleMappingsPage = () => {
  const { myUser, userAuthenticatedApiCall } = useUser();
  const navigate = useNavigateSafe();
  const { identityProviderId } = useParams();

  const [roleMappings, setRoleMappings] =
    useState<SSORoleMappingsSummary | null>(null);
  const [isDeletingRoleMapping, setIsDeletingRoleMapping] = useState(false);
  const [deleteRoleKeyPrompt, setDeleteRoleKeyPrompt] = useState<{
    s2ClaimName: string;
    comparisonType: string;
    propertyValue: string;
  } | null>(null);

  useAsyncEffect(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    async (isAlive) => {
      if (!isAlive() || !myUser || myUser.roleAssignments !== 'superAdmin') {
        return;
      }

      const getResult = await userAuthenticatedApiCall({
        url: `${getApiBaseUrl()}/admin/sso/identity-providers/${identityProviderId}/role-mappings`,
        requestOptions: {
          method: 'GET',
        },
        responseSchema: SSORoleMappingsSummary,
      });

      if (!isAlive()) {
        return;
      }

      if (!getResult) {
        return;
      }

      setRoleMappings(getResult.resultType === 'err' ? null : getResult.value);
    },
    [myUser, userAuthenticatedApiCall, identityProviderId],
  );

  const deleteRoleMapping = useAsyncCallback(
    async (isAlive: IsAliveFn) => {
      if (!isAlive() || !deleteRoleKeyPrompt) {
        return;
      }

      try {
        setIsDeletingRoleMapping(true);

        const deleteResult = await userAuthenticatedApiCall({
          url: `${getApiBaseUrl()}/admin/sso/identity-providers/${identityProviderId}/role-mappings/role-mapping?s2ClaimName=${encodeURIComponent(
            deleteRoleKeyPrompt.s2ClaimName,
          )}&propertyValue=${encodeURIComponent(
            deleteRoleKeyPrompt.propertyValue,
          )}&comparisonType=${encodeURIComponent(
            deleteRoleKeyPrompt.comparisonType,
          )}`,
          requestOptions: {
            method: 'DELETE',
          },
          responseSchema: EmptyObject,
        });

        if (!isAlive()) {
          return;
        }

        if (!deleteResult || deleteResult.resultType === 'err') {
          return;
        }

        setRoleMappings((prevRoleMappings) => {
          if (!prevRoleMappings) {
            return prevRoleMappings;
          }

          return {
            ...prevRoleMappings,
            ssoRoleMappings: prevRoleMappings.ssoRoleMappings.filter(
              (c) =>
                c.s2ClaimName !== deleteRoleKeyPrompt.s2ClaimName &&
                c.propertyValue !== deleteRoleKeyPrompt.propertyValue &&
                c.comparisonType !== deleteRoleKeyPrompt.comparisonType,
            ),
          };
        });
      } finally {
        setIsDeletingRoleMapping(false);
        setDeleteRoleKeyPrompt(null);
      }
    },
    [deleteRoleKeyPrompt, identityProviderId, userAuthenticatedApiCall],
  );

  const rows = useMemo(() => {
    if (!roleMappings) {
      return [];
    }

    const actualRows: AdminTableRow[] = [];

    for (const mapping of roleMappings.ssoRoleMappings) {
      actualRows.push({
        key: `${mapping.s2ClaimName}-${mapping.comparisonType}-${mapping.propertyValue}`,
        columns: [
          {
            columnType: 'text',
            key: 'claim_name',
            text: mapping.s2ClaimName,
          },
          {
            columnType: 'text',
            key: 'comparison_type',
            text: mapping.comparisonType,
          },
          {
            columnType: 'text',
            key: 'claim_value',
            text: mapping.propertyValue,
          },
          {
            columnType: 'button',
            key: 'edit',
            buttonText: 'Edit',
            onClick: () => {
              navigate(
                `/sso-main/identity-provider/${identityProviderId}/role-mapping/existing?s2ClaimName=${encodeURIComponent(
                  mapping.s2ClaimName,
                )}&propertyValue=${encodeURIComponent(
                  mapping.propertyValue,
                )}&comparisonType=${encodeURIComponent(
                  mapping.comparisonType,
                )}`,
              );
            },
          },
          {
            columnType: 'button',
            key: 'delete',
            buttonText: 'Delete',
            onClick: () =>
              setDeleteRoleKeyPrompt({
                s2ClaimName: mapping.s2ClaimName,
                comparisonType: mapping.comparisonType,
                propertyValue: mapping.propertyValue,
              }),
          },
        ],
      });
    }
    return actualRows;
  }, [roleMappings, navigate, identityProviderId]);

  const ui = useMemo(() => {
    if (isDeletingRoleMapping) {
      return <p>Deleting role mapping...</p>;
    }

    return (
      <>
        <p>You can manage SSO role mappings from this screen.</p>

        <div className="flex flex-col items-start">
          <LinkButton
            variant={LegacyButtonVariant.PRIMARY_LIGHT}
            href={`/sso-main/identity-provider/${identityProviderId}/role-mapping/new`}
            data-qa="newUserBtn"
            className="mt-4"
          >
            New Role Mapping
          </LinkButton>
        </div>

        <AdminTable
          headings={[
            'S2 Claim Name',
            'Comparison',
            'Property Value',
            'Edit',
            'Delete',
          ]}
          rows={rows}
        />

        <Transition appear show={!!deleteRoleKeyPrompt} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-overlay"
            onClose={() => setDeleteRoleKeyPrompt(null)}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-12px transition-all">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900"
                    >
                      Are you sure?
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete this role mapping? You
                        will not be able to recover anything.
                      </p>
                    </div>

                    <div className="mt-4">
                      <LegacyButton
                        variant={LegacyButtonVariant.PRIMARY_LIGHT}
                        onClick={() => deleteRoleMapping()}
                        disabled={isDeletingRoleMapping}
                        className="mt-4"
                      >
                        Delete
                      </LegacyButton>

                      <LegacyButton
                        variant={LegacyButtonVariant.PRIMARY_LIGHT}
                        onClick={() => setDeleteRoleKeyPrompt(null)}
                        disabled={isDeletingRoleMapping}
                        className="mt-4 ml-4"
                      >
                        Cancel
                      </LegacyButton>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </>
    );
  }, [
    isDeletingRoleMapping,
    rows,
    deleteRoleKeyPrompt,
    deleteRoleMapping,
    identityProviderId,
  ]);

  return (
    <AdminLayout currentPage={'AdminSSORoleMappings'}>
      <AdminBreadcrumbs
        items={[
          { label: 'Home', to: '/admin' },
          { label: 'SSO', to: '/sso-main' },
          {
            label: 'Identity Provider',
            to: `/sso-main/identity-provider/${identityProviderId}`,
          },
          { label: 'Role Mappings', to: '' },
        ]}
      />
      {ui}
    </AdminLayout>
  );
};
